import { observable, computed } from 'mobx';

import { Model, App, ArrayApp } from 'core/index';

import { ContentModel, ContentApp } from 'apps/content/app';

import { OneColumnLayout } from 'apps/layout';

export class ActivityModel extends Model {
  static registryName = 'ActivityModel';
  static ModelProps = {
    origin: ContentModel,
    content: ContentModel,
  };

  @observable origin = null;
  @observable content = null;
  @observable date = null;
  @observable action;
}

export class ActivityApp extends App {
  static registryName = 'ActivityApp';
  static AppProps = {
    origin: ContentApp,
    content: ContentApp,
  };

  static Model = ActivityModel;
  @computed get hasContentPreview() {
    const hasPreviewTypes = ['contribution', 'issue'];
    if (hasPreviewTypes.includes(this.content.type)) {
      return true;
    }
    return false;
  }

  @computed get icon() {
    if (this.action === 'created') {
      switch (this.content.type) {
        case 'collection':
          return 'collection';
        case 'story':
          return 'story';
        case 'contribution':
          return 'contribution';
        case 'issue':
          return 'issue_open';
        case 'project':
          return 'folder';
        case 'initiative':
          return 'organisation';
        case 'comment':
          return 'comment';
        case 'post':
          return 'chat_multiple';
        default:
          return '';
      }
    }
    return 'user';
  }

  @computed get subject() {
    if (this.action === 'created') {
      if (this.content.type === 'comment') {
        return {
          name: this.content.creator.username,
          avatar: this.content.creator.profile.avatar,
          routeName: 'profile',
          routeParams: {
            profile: this.content.creator.username,
          },
        };
      }
      if (this.content.parentContent.type === 'initiative') {
        return {
          name: `+${this.content.parentContent.slug}`,
          avatar: this.content.parentContent.avatar,
          routeName: 'initiative',
          routeParams: {
            initiative: `+${this.content.parentContent.slug}`,
          },
        };
      }
      return {
        name: this.content.creator.username,
        avatar: this.content.creator.profile.avatar,
        routeName: 'profile',
        routeParams: {
          profile: this.content.creator.username,
        },
      };
    }
    return {
      name: `@${this.origin.slug}`,
      avatar: this.origin.avatar,
      routeName: 'profile',
      routeParams: {
        profile: `@${this.origin.slug}`,
      },
    };
  }

  @computed get predicate() {
    if (this.action === 'created') {
      switch (this.content.type) {
        case 'story':
          return 'published';
        case 'project':
          return 'created the project';
        case 'contribution':
          return 'made a contribution to';
        case 'issue':
          return 'opened an issue in';
        case 'collection':
          return 'created the collection';
        case 'initiative':
          return 'created the organisation';
        case 'post':
          return 'posted';
        case 'profile':
          return 'followed';
        case 'comment':
          return 'commented on';
        default:
          return '';
      }
    } else if (this.action === 'followed') {
      return 'followed';
    }
    return '';
  }

  // FIXME - Cleanup
  // eslint-disable-next-line
  @computed get object() {
    if (this.action === 'created') {
      const typesInProjects = ['contribution', 'issue'];
      if (typesInProjects.includes(this.content.type)) {
        const projectSpace = this.content.parentContent.parentContent;
        let prefix = '@';
        let routeName = 'profile.project';
        if (projectSpace.type === 'initiative') {
          prefix = '+';
          routeName = 'initiative.project';
        }
        return {
          name: `${prefix + projectSpace.slug}/${
            this.content.parentContent.slug
          }`,
          routeName,
          routeParams: {
            [projectSpace.type]: prefix + projectSpace.slug,
            slug: this.content.parentContent.slug,
          },
        };
      }
      let { title } = this.content;
      if (this.content.type === 'project') {
        const prefix =
          this.content.parentContent.type === 'profile' ? '@' : '+';
        title = `${prefix}${this.content.parentContent.slug}/${this.content.slug}`;
      }
      if (this.content.type === 'initiative') {
        return {
          name: title,
          routeName: 'initiative',
          routeParams: {
            initiative: `+${this.content.slug}`,
          },
        };
      }
      if (this.content.type === 'post') {
        return {
          name: title,
          routeName: 'home.post',
          routeParams: {
            slug: this.content.slug,
          },
        };
      }
      if (this.content.type === 'comment') {
        if (this.content.origin.type === 'post') {
          return {
            name: title,
            routeName: 'home.post',
            routeParams: {
              slug: this.content.origin.slug,
            },
          };
        }
        let prefix = '@';
        let spaceName = 'profile';
        if (this.content.origin.parentContent.type === 'initiative') {
          prefix = '+';
          spaceName = 'initiative';
        }
        if (this.content.origin.type === 'story') {
          return {
            name: title,
            routeName: `${spaceName}.${this.content.origin.type}`,
            routeParams: {
              [spaceName]: prefix + this.content.origin.parentContent.slug,
              slug: this.content.origin.slug,
            },
          };
        }
        spaceName = 'profile.project';
        if (
          this.content.origin.parentContent.parentContent.type === 'initiative'
        ) {
          spaceName = 'initiative.project';
          prefix = '+';
        }
        if (this.content.origin.type === 'issue') {
          return {
            name: title,
            routeName: `${spaceName}.issue`,
            routeParams: {
              [spaceName.split('.')[0]]:
                prefix + this.content.origin.parentContent.parentContent.slug,
              slug: this.content.origin.parentContent.slug,
              issueSlug: this.content.origin.slug,
            },
          };
        }
        if (this.content.origin.type === 'contribution') {
          return {
            name: title,
            routeName: `${spaceName}.contribution`,
            routeParams: {
              [spaceName.split('.')[0]]:
                prefix + this.content.origin.parentContent.parentContent.slug,
              slug: this.content.origin.parentContent.slug,
              contributionId: this.content.origin.version,
            },
          };
        }
        return {
          name: title,
          routeName: `${this.content.origin.parentContent.type}.${this.content.origin.type}`,
          routeParams: {},
        };
      }
      return {
        name: title,
        routeName: `${this.content.parentContent.type}.${this.content.type}`,
        routeParams: {
          [this.content.parentContent.type]: this.subject.name,
          slug: this.content.slug,
        },
      };
    }
    if (this.content.type === 'profile') {
      return {
        name: `@${this.content.slug}`,
        routeName: 'profile',
        routeParams: {
          profile: `@${this.content.slug}`,
        },
      };
    }
    if (this.content.type === 'initiative') {
      return {
        name: `+${this.content.slug}`,
        routeName: 'initiative',
        routeParams: {
          initiative: `+${this.content.slug}`,
        },
      };
    }
    if (this.content.type === 'collection') {
      return {
        name: this.content.title,
        routeName: 'profile.collection',
        routeParams: {
          profile: this.content.creator.username,
          slug: this.content.slug,
        },
      };
    }
    if (this.content.type === 'project') {
      if (this.content.parentContent.type === 'initiative') {
        return {
          name: `+${this.content.parentContent.slug}/${this.content.slug}`,
          routeName: 'initiative.project',
          routeParams: {
            initiative: `+${this.content.parentContent.slug}`,
            slug: this.content.slug,
          },
        };
      }
      return {
        name: `@${this.content.parentContent.slug}/${this.content.slug}`,
        routeName: 'profile.project',
        routeParams: {
          profile: `@${this.content.parentContent.slug}`,
          slug: this.content.slug,
        },
      };
    }
  }
}

export class ActivitiesApp extends ArrayApp {
  static registryName = 'ActivitiesApp';
  static ElementApp = ActivityApp;
  cardLayout = new OneColumnLayout();
}
